import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Toaster } from "sonner";
import { ArrowRight } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import AboutModal from "@/components/AboutModal";
import SlotsLeftTicker from "@/components/SlotsLeftTicker";
import GoogleAuthButton from "@/components/GoogleAuthButton";
import ContinuousBrandStrip from "@/components/ContinuousBrandStrip";
import PWAInstallBanner from "@/components/PWAInstallBanner";
import ChatPreview from "@/components/ChatPreview";
import { tapFeedback } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";

// Light health-green landing palette (2026-06-25 overhaul).
// The authed dashboard stays on the original dark palette — this palette is
// ONLY used inside Landing.jsx and the components that accept `light={true}`.
const C = {
  pageBg: "#F4FAF2",
  altBg: "#EAF3DE",
  cardBg: "#FFFFFF",
  borderSoft: "#D4EAD0",
  borderStrong: "#C0DD97",
  textPrimary: "#111111",
  textSecondary: "#3B6D11",
  textMuted: "#639922",
  saffron: "#FF6B00",
  saffronHover: "#E05500",
  badgeBg: "#EAF3DE",
  badgeText: "#27500A",
};

// Three "How it works" steps shown in the left column of the
// "How it works — and what you discover" section.
const HOW_STEPS = [
  {
    title: "Upload invoices or screenshots",
    body: "PDF invoices from Blinkit or Zepto, or screenshots from Swiggy or Zomato. Any format, any app. Dark mode screenshots work too.",
  },
  {
    title: "AI analyses in 60 seconds",
    body: "Claude AI reads every item, price and category across all your food apps simultaneously. No manual entry. No spreadsheets.",
  },
  {
    title: "See your complete food report",
    body: "Category breakdown, platform split, top items with restock predictions, smart weekly list, and monthly budget tracking.",
  },
];

// Five "intelligence feed" cards shown in the right column.
const INSIGHTS = [
  {
    color: "#FF6B00",
    title: "Friday craving pattern",
    quote:
      "You order snacks and comfort food 73% of Friday evenings after 8pm — likely after a long work week.",
  },
  {
    color: "#1D9E75",
    title: "Budget vs actual spend",
    quote:
      "You budgeted ₹9,000 this month but will hit ₹14,200 by month end at your current pace — 6 days before salary.",
  },
  {
    color: "#378ADD",
    title: "Mid-week ordering spike",
    quote:
      "Your Blinkit orders spike every Wednesday — 3 separate orders vs 1 on other days. One planned shop could save ₹800/month.",
  },
  {
    color: "#BA7517",
    title: "Food waste signal",
    quote:
      "You buy 2kg tomatoes every week but reorder before they can be used. Estimated waste: ₹380/month.",
  },
  {
    color: "#534AB7",
    title: "The savings opportunity",
    quote:
      "Switch Red Bull to once a week instead of 4 times — save ₹1,800/month. That is ₹21,600/year. One Goa trip.",
  },
];

// Suggestion chips shown below the ChatPreview. First two carry the
// health-focused green tint, last two are neutral spending-focused.
const SUGGESTION_CHIPS = [
  { label: "Is my family eating healthy?", tone: "green" },
  { label: "What is my nutrition score?", tone: "green" },
  { label: "How much do I spend on food?", tone: "neutral" },
  { label: "Show my ordering patterns", tone: "neutral" },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [aboutOpen, setAboutOpen] = useState(false);
  const chatPreviewRef = useRef(null);

  // Capture ?ref=CODE from URL into localStorage so we can claim it after the
  // user signs in via Google OAuth (the OAuth redirect strips query params).
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get("ref");
      if (ref && /^[A-Z0-9]{8}$/.test(ref.toUpperCase())) {
        localStorage.setItem("cartiq_referral_code", ref.toUpperCase());
      }
    } catch {
      /* ignore */
    }
  }, []);

  // The bottom-nav "About" tab dispatches this event so we don't have to
  // thread setAboutOpen through a global context.
  useEffect(() => {
    const open = () => setAboutOpen(true);
    window.addEventListener("cartiq:open-about", open);
    return () => window.removeEventListener("cartiq:open-about", open);
  }, []);

  // Primary hero CTA → sends the user to the dedicated /upload route, which
  // now houses the upload zone (the zone used to be inline on this page).
  const goToUpload = () => {
    tapFeedback();
    navigate("/upload");
  };

  // Suggestion chips no longer navigate directly — they inject their label as
  // a user message into the ChatPreview thread, which then streams the
  // matching canned response and reveals the "See your real intelligence →"
  // CTA. This turns the hero into a live demo without any real API calls.
  const askChatPreview = (label) => {
    tapFeedback();
    chatPreviewRef.current?.ask?.(label);
    // Scroll the chat widget into view so mobile users see it react.
    const el = document.querySelector('[data-testid="chat-preview-wrapper"]');
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  };
  void user;

  return (
    <div
      className="min-h-screen relative"
      style={{ background: C.pageBg, color: C.textPrimary }}
      data-testid="landing-page"
    >
      <Toaster position="top-center" richColors theme="light" />

      {/* ─────────────────────── HEADER / NAV ─────────────────────── */}
      <header
        className="sticky top-0 z-30 backdrop-blur"
        style={{
          background: `${C.pageBg}CC`,
          borderBottom: `0.5px solid ${C.borderSoft}`,
        }}
        data-testid="landing-header"
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <BrandMark light />
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            <button
              type="button"
              onClick={() => setAboutOpen(true)}
              aria-label="About Us"
              className="text-[10px] sm:text-xs uppercase tracking-[0.14em] sm:tracking-[0.18em] font-bold px-2 sm:px-3 py-1.5 sm:py-2 border transition-colors whitespace-nowrap rounded-sm"
              style={{ borderColor: C.borderStrong, color: C.textSecondary }}
              data-testid="nav-about-btn"
            >
              <span className="sm:hidden">About</span>
              <span className="hidden sm:inline">About Us</span>
            </button>
            <Link
              to="/pricing"
              className="hidden md:block text-sm uppercase tracking-wider font-semibold transition-colors"
              style={{ color: C.textSecondary }}
              data-testid="nav-pricing"
            >
              Pricing
            </Link>
            <GoogleAuthButton target="/" />
            <button
              type="button"
              className="text-xs sm:text-sm px-2.5 sm:px-4 py-2 whitespace-nowrap font-bold text-white rounded-sm uppercase tracking-[0.06em] transition-colors"
              style={{ background: C.saffron }}
              onMouseEnter={(e) => (e.currentTarget.style.background = C.saffronHover)}
              onMouseLeave={(e) => (e.currentTarget.style.background = C.saffron)}
              onClick={goToUpload}
              data-testid="nav-cta-btn"
            >
              <span className="sm:hidden">Try Free →</span>
              <span className="hidden sm:inline">Try Free →</span>
            </button>
          </div>
        </div>
      </header>

      <AboutModal open={aboutOpen} onClose={() => setAboutOpen(false)} />

      {/* ────────────────────────── HERO ────────────────────────── */}
      <section
        id="landing-hero"
        className="relative"
        data-testid="landing-hero"
      >
        <div className="max-w-3xl mx-auto px-6 pt-14 md:pt-20 pb-6 text-center">
          {/* Eyebrow badge */}
          <div
            className="inline-flex items-center gap-2 px-3 py-1 text-[11px] uppercase tracking-[0.22em] font-bold rounded-full"
            style={{ background: C.badgeBg, color: C.badgeText }}
            data-testid="hero-eyebrow"
          >
            India&apos;s food intelligence platform
          </div>

          {/* Headline — two lines, saffron accent on line 2 */}
          <h1
            className="font-display mt-6 text-[clamp(38px,7vw,68px)] leading-[1.02] tracking-tight font-black"
            style={{ color: C.textPrimary }}
            data-testid="hero-headline"
          >
            Know what you eat.
            <br />
            <span style={{ color: C.saffron }}>Know where your money goes.</span>
          </h1>

          {/* Subheadline */}
          <p
            className="mt-6 text-base md:text-lg max-w-2xl mx-auto leading-[1.6]"
            style={{ color: C.textSecondary }}
            data-testid="hero-subheadline"
          >
            CartIQ 360 combines food spending intelligence with health
            intelligence. Upload an invoice or screenshot from any food app —
            or simply ask. Complete clarity in 60 seconds.
          </p>

          {/* Primary CTA */}
          <div className="mt-8 flex justify-center">
            <button
              type="button"
              onClick={goToUpload}
              className="inline-flex items-center justify-center gap-2 text-sm md:text-base px-7 py-3.5 md:px-9 md:py-4 font-bold text-white rounded-sm uppercase tracking-[0.08em] transition-colors shadow-sm"
              style={{ background: C.saffron }}
              onMouseEnter={(e) => (e.currentTarget.style.background = C.saffronHover)}
              onMouseLeave={(e) => (e.currentTarget.style.background = C.saffron)}
              data-testid="hero-cta-btn"
            >
              Try CartIQ Free <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
            </button>
          </div>

          {/* Trust row */}
          <div
            className="mt-5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 text-[12px] font-medium"
            style={{ color: C.textMuted }}
            data-testid="hero-trust-row"
          >
            <span>No app download</span>
            <span style={{ color: C.borderStrong }}>·</span>
            <span>No signup required</span>
            <span style={{ color: C.borderStrong }}>·</span>
            <span>Works with all food apps</span>
          </div>
        </div>

        {/* Chat preview */}
        <div className="max-w-3xl mx-auto px-4 pb-4">
          <ChatPreview ref={chatPreviewRef} />
        </div>

        {/* Suggestion chips — click any chip to trigger a live chat demo
            reply inside the ChatPreview above, no navigation. */}
        <div className="max-w-3xl mx-auto px-4 pb-8">
          <div
            className="mx-auto max-w-[500px] grid grid-cols-2 gap-2 md:gap-2.5"
            data-testid="suggestion-chips"
          >
            {SUGGESTION_CHIPS.map((chip) => {
              const isGreen = chip.tone === "green";
              return (
                <button
                  key={chip.label}
                  type="button"
                  onClick={() => askChatPreview(chip.label)}
                  className="text-left text-[12px] md:text-[13px] font-medium px-3 py-2.5 rounded-full border transition-all hover:shadow-sm hover:-translate-y-[1px]"
                  style={{
                    background: isGreen ? C.badgeBg : "#FFFFFF",
                    borderColor: isGreen ? C.borderStrong : C.borderSoft,
                    color: isGreen ? C.textSecondary : "#555555",
                  }}
                  data-testid={`suggestion-chip-${chip.label
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, "-")
                    .replace(/^-+|-+$/g, "")}`}
                >
                  {chip.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Slots ticker */}
        <div className="max-w-3xl mx-auto px-6 flex justify-center" data-testid="hero-slots-ticker">
          <SlotsLeftTicker compact={false} light />
        </div>

        {/* Wallet coming-soon strip */}
        <div className="max-w-3xl mx-auto px-6 mt-4 flex justify-center">
          <div
            className="inline-flex flex-wrap items-center justify-center gap-3 px-4 py-2.5 rounded-sm"
            style={{
              background: C.altBg,
              border: `0.5px solid ${C.borderStrong}`,
            }}
            data-testid="wallet-coming-soon"
          >
            <span
              className="text-[10px] uppercase tracking-[0.22em] font-bold"
              style={{ color: C.textSecondary }}
            >
              <span className="mr-1">⚡</span>Wallet · Coming Soon
            </span>
            <span
              className="hidden sm:block text-xs"
              style={{ color: C.textMuted }}
            >
              Connect{" "}
              <span className="font-bold" style={{ color: C.textSecondary }}>
                PhonePe
              </span>{" "}
              ·{" "}
              <span className="font-bold" style={{ color: C.textSecondary }}>
                Google Pay
              </span>{" "}
              ·{" "}
              <span className="font-bold" style={{ color: C.textSecondary }}>
                Paytm
              </span>{" "}
              — auto-track every UPI food spend
            </span>
            <span
              className="sm:hidden text-xs"
              style={{ color: C.textMuted }}
            >
              <span className="font-bold" style={{ color: C.textSecondary }}>
                PhonePe
              </span>{" "}
              ·{" "}
              <span className="font-bold" style={{ color: C.textSecondary }}>
                GPay
              </span>{" "}
              ·{" "}
              <span className="font-bold" style={{ color: C.textSecondary }}>
                Paytm
              </span>
            </span>
          </div>
        </div>

        {/* Brand strip */}
        <div
          className="mt-10"
          style={{
            background: C.altBg,
            borderTop: `1px solid ${C.borderStrong}`,
            borderBottom: `1px solid ${C.borderStrong}`,
          }}
          data-testid="landing-brand-strip"
        >
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div
              className="text-[10px] uppercase tracking-[0.22em] mb-3 text-center font-bold"
              style={{ color: C.textMuted }}
            >
              Works with every food &amp; grocery app in India
            </div>
            <ContinuousBrandStrip light />
          </div>
        </div>
      </section>

      {/* ─────────────── HOW IT WORKS — TWO COLUMN ─────────────── */}
      <section
        id="how-it-works"
        style={{
          background: C.pageBg,
          borderTop: `1px solid ${C.borderSoft}`,
        }}
        data-testid="how-it-works-section"
      >
        <div className="max-w-7xl mx-auto px-6 py-14 md:py-20">
          <div className="text-center max-w-3xl mx-auto">
            <h2
              className="font-display text-3xl md:text-4xl tracking-tight font-black"
              style={{ color: C.textPrimary }}
              data-testid="how-it-works-heading"
            >
              How it works — and what you{" "}
              <span style={{ color: C.saffron }}>discover</span>
            </h2>
            <p
              className="mt-4 text-base md:text-lg"
              style={{ color: C.textSecondary }}
              data-testid="how-it-works-subheading"
            >
              Upload once. The intelligence never stops surprising you.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12">
            {/* LEFT — 3 steps */}
            <div data-testid="how-it-works-left">
              <div
                className="text-[11px] uppercase tracking-[0.22em] font-bold pb-2 mb-6"
                style={{
                  color: C.saffron,
                  borderBottom: `1px solid ${C.saffron}66`,
                }}
                data-testid="how-it-works-left-heading"
              >
                3 steps to get started
              </div>
              <div className="space-y-4">
                {HOW_STEPS.map((s, i) => (
                  <div
                    key={i}
                    className="flex gap-4 p-5 md:p-6 rounded-sm shadow-sm"
                    style={{
                      background: C.altBg,
                      border: `1px solid ${C.borderStrong}`,
                    }}
                    data-testid={`how-step-${i}`}
                  >
                    <div
                      className="flex-shrink-0 w-9 h-9 md:w-10 md:h-10 rounded-full text-white font-bold font-mono-num flex items-center justify-center text-sm md:text-base"
                      style={{ background: C.saffron }}
                      aria-hidden="true"
                    >
                      {i + 1}
                    </div>
                    <div className="min-w-0">
                      <div
                        className="font-bold text-[15px] md:text-base leading-snug"
                        style={{ color: C.textPrimary }}
                      >
                        {s.title}
                      </div>
                      <div
                        className="text-[13px] md:text-sm mt-1.5 leading-relaxed"
                        style={{ color: C.textSecondary }}
                      >
                        {s.body}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT — 5 insight cards */}
            <div data-testid="how-it-works-right">
              <div
                className="text-[11px] uppercase tracking-[0.22em] font-bold pb-2 mb-6"
                style={{
                  color: C.saffron,
                  borderBottom: `1px solid ${C.saffron}66`,
                }}
                data-testid="how-it-works-right-heading"
              >
                What CartIQ 360 reveals about you
              </div>
              <div className="space-y-3">
                {INSIGHTS.map((c, i) => (
                  <div
                    key={i}
                    className="px-4 py-3.5 md:px-5 md:py-4 rounded-sm shadow-sm"
                    style={{
                      background: C.cardBg,
                      border: `1px solid ${C.borderSoft}`,
                      borderLeft: `3px solid ${c.color}`,
                    }}
                    data-testid={`insight-card-${i}`}
                  >
                    <div className="flex items-center gap-2.5">
                      <span
                        className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
                        style={{
                          background: c.color,
                          boxShadow: `0 0 0 2px ${c.color}22`,
                        }}
                        aria-hidden="true"
                      />
                      <div
                        className="font-bold text-[14px] md:text-[15px] leading-snug"
                        style={{ color: C.textPrimary }}
                      >
                        {c.title}
                      </div>
                    </div>
                    <div
                      className="mt-1.5 text-[12.5px] md:text-[13px] leading-relaxed pl-[16px]"
                      style={{ color: C.textSecondary }}
                    >
                      {c.quote}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────── FIDUCIARY CALLOUT ─────────────── */}
      <section
        style={{ borderTop: `1px solid ${C.borderSoft}` }}
        data-testid="fiduciary-callout"
      >
        <div className="max-w-3xl mx-auto px-6 py-10 md:py-14 text-center">
          <Link
            to="/smart-cart"
            className="inline-block group"
            data-testid="fiduciary-callout-link"
          >
            <p
              className="text-lg md:text-xl leading-snug tracking-tight"
              style={{
                color: C.textSecondary,
                fontFamily: "'Pirata One', serif",
              }}
            >
              See how CartIQ acts as your{" "}
              <span style={{ color: C.textPrimary }} className="font-bold">
                fiduciary health agent.
              </span>{" "}
              <span
                className="group-hover:underline"
                style={{ color: C.saffron }}
              >
                Try the AI Demo →
              </span>
            </p>
            <span
              className="block mt-2 text-[10px] uppercase tracking-[0.32em]"
              style={{ color: C.textMuted }}
            >
              No sign-in needed · 60 seconds
            </span>
          </Link>
        </div>
      </section>

      {/* ─────────────── FOOTER ─────────────── */}
      <footer
        className="mt-2"
        style={{
          background: C.altBg,
          borderTop: `1px solid ${C.borderStrong}`,
        }}
        data-testid="landing-footer"
      >
        <div
          className="max-w-7xl mx-auto px-6 py-8 flex flex-col md:flex-row justify-between gap-4 text-xs uppercase tracking-[0.18em]"
          style={{ color: C.textMuted }}
        >
          <div>
            <span className="font-bold" style={{ color: C.saffron }}>
              CartIQ 360
            </span>{" "}
            · Solutionist
          </div>
          <div className="flex flex-wrap items-center gap-5">
            <Link
              to="/intelligence"
              className="hover:opacity-80 transition-opacity"
              style={{ color: C.textSecondary }}
              data-testid="footer-intelligence-link"
            >
              Intelligence
            </Link>
            <Link
              to="/pricing"
              className="hover:opacity-80 transition-opacity"
              style={{ color: C.textSecondary }}
              data-testid="footer-pricing-link"
            >
              Pricing
            </Link>
            <button
              type="button"
              onClick={() => setAboutOpen(true)}
              className="hover:opacity-80 transition-opacity uppercase tracking-[0.18em]"
              style={{ color: C.textSecondary }}
              data-testid="footer-about-link"
            >
              About
            </button>
            <Link
              to="/terms"
              className="hover:opacity-80 transition-opacity"
              style={{ color: C.textSecondary }}
              data-testid="footer-terms-link"
            >
              Terms
            </Link>
            <Link
              to="/privacy"
              className="hover:opacity-80 transition-opacity"
              style={{ color: C.textSecondary }}
              data-testid="footer-privacy-link"
            >
              Privacy
            </Link>
            <Link
              to="/feedback"
              className="hover:opacity-80 transition-opacity"
              style={{ color: C.textSecondary }}
              data-testid="footer-feedback-link"
            >
              Feedback
            </Link>
            <a
              href="mailto:support@solutionist.co.in"
              className="hover:opacity-80 transition-opacity normal-case tracking-normal"
              style={{ color: C.textSecondary }}
              data-testid="footer-contact-link"
            >
              support@solutionist.co.in
            </a>
          </div>
          <div className="hidden md:block">
            Born in India · Built for the World.
          </div>
        </div>
      </footer>

      {/* Mobile bottom navigation was intentionally removed from the public
          Landing page on 2026-06-26 so the page flows cleanly into the
          footer. Authenticated Dashboard still uses its own bottom-nav
          — that lives outside this file and is unaffected. */}

      {/* PWA install banner — shown only on Home, only on mobile, after 30s */}
      <PWAInstallBanner />
    </div>
  );
}
